import { logging, API_BASE_URL } from '@/config/env';
import {
  MetaTag,
  MetaTagListResponse,
  ApiMetaTagListResponse,
  MetaTagQueryParams,
  CreateMetaTagRequest,
  UpdateMetaTagRequest,
} from './seo.dto';

// Custom logging function
const customLog = (message: string, ...args: unknown[]) => {
  if (logging) {
    console.log(`[SEO Service] ${message}`, ...args);
  }
};

class SEOService {
  private baseUrl = API_BASE_URL;
  private logoutCallback?: () => void;

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    customLog('=== API Request ===');
    customLog('URL:', url);
    customLog('Method:', options.method || 'GET');

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    customLog('Response status:', response.status);
    customLog('Response ok:', response.ok);

    if (!response.ok) {
      const errorText = await response.text();
      customLog('Error response:', errorText);

      // Try to parse error response to check for session expired
      try {
        const errorData = JSON.parse(errorText);
        if (
          errorData.status === false &&
          errorData.message === 'Invalid or expired admin session'
        ) {
          customLog('Session expired, triggering logout');
          if (this.logoutCallback) {
            this.logoutCallback();
          }
        }
      } catch {
        // If parsing fails, continue with original error handling
      }

      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    customLog('Response data:', JSON.stringify(data, null, 2));

    // Check for session expired in successful response
    if (
      data.status === false &&
      data.message === 'Invalid or expired admin session'
    ) {
      customLog('Session expired in response, triggering logout');
      if (this.logoutCallback) {
        this.logoutCallback();
      }
    }

    return data;
  }

  // Meta Tag Management APIs
  async getMetaTags(
    token: string,
    params: MetaTagQueryParams = {}
  ): Promise<MetaTagListResponse> {
    customLog('API Service: getMetaTags called');
    customLog('API Service: Token:', token ? 'Present' : 'Missing');
    customLog('API Service: Params:', params);

    const queryString = new URLSearchParams();

    if (params.page) queryString.append('page', params.page.toString());
    if (params.limit) queryString.append('limit', params.limit.toString());
    if (params.search) queryString.append('search', params.search);
    if (params.page_slug) queryString.append('page_slug', params.page_slug);
    if (params.sortBy) queryString.append('sortBy', params.sortBy);
    if (params.sortOrder) queryString.append('sortOrder', params.sortOrder);

    const endpoint = `/admin/meta${
      queryString.toString() ? `?${queryString.toString()}` : ''
    }`;

    customLog('API Service: Final endpoint:', endpoint);

    const response = await this.request<ApiMetaTagListResponse>(endpoint, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    customLog('=== API Service Response Analysis ===');
    customLog('Full response object:', response);
    customLog('response keys:', Object.keys(response));
    customLog('response status:', response.status as unknown);
    customLog('response message:', response.message as unknown);
    customLog('response data:', response.data);
    customLog('response.data type:', typeof response.data);

    // Check if response.data exists
    if (!response.data) {
      customLog('API Service: Warning - response.data is undefined');
      return {
        data: [],
        meta: { total: 0, page: 1, lastPage: 1, limit: 10 },
      };
    }

    // Check if response.data has the nested structure or is flat
    const responseData = response.data as unknown as
      | MetaTag[]
      | {
          data: MetaTag[];
          meta?: {
            total: number;
            page: number;
            lastPage: number;
            limit: number;
          };
        };

    // If response.data is an array (flat structure), use response metadata
    if (Array.isArray(responseData)) {
      const result = {
        data: responseData,
        meta: {
          total: response.total || 0,
          page: response.page || 1,
          lastPage: response.lastPage || 1,
          limit: params.limit || 10,
        },
      };

      customLog('=== Flat Structure Result ===');
      customLog('result.data length:', result.data.length);
      customLog('result.meta:', result.meta);

      return result;
    }

    // Handle nested structure
    const result = {
      data: responseData.data || responseData || [],
      meta: responseData.meta || {
        total: response.total || 0,
        page: response.page || 1,
        lastPage: response.lastPage || 1,
        limit: params.limit || 10,
      },
    };

    customLog('=== Final Parsed Result ===');
    customLog('result.data length:', result.data.length);
    customLog('result.meta:', result.meta);

    return result;
  }

  async getMetaTagById(token: string, id: number): Promise<MetaTag> {
    customLog('API Service: getMetaTagById called with ID:', id);

    const response = await this.request<{ status: boolean; data: MetaTag }>(
      `/admin/meta/${id}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.data) {
      throw new Error('Meta tag not found');
    }

    return response.data;
  }

  async getMetaTagBySlug(token: string, page_slug: string): Promise<MetaTag> {
    customLog('API Service: getMetaTagBySlug called with slug:', page_slug);

    const response = await this.request<{ status: boolean; data: MetaTag }>(
      `/admin/meta/slug/${page_slug}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.data) {
      throw new Error('Meta tag not found');
    }

    return response.data;
  }

  async createMetaTag(
    token: string,
    metaTagData: CreateMetaTagRequest
  ): Promise<MetaTag> {
    customLog('API Service: createMetaTag called with data:', metaTagData);

    const response = await this.request<{ status: boolean; data: MetaTag }>(
      '/admin/meta',
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(metaTagData),
      }
    );

    if (!response.data) {
      throw new Error('Failed to create meta tag');
    }

    return response.data;
  }

  async updateMetaTag(
    token: string,
    id: number,
    metaTagData: UpdateMetaTagRequest
  ): Promise<MetaTag> {
    customLog(
      'API Service: updateMetaTag called with ID:',
      id,
      'and data:',
      metaTagData
    );

    const response = await this.request<{ status: boolean; data: MetaTag }>(
      `/admin/meta/${id}`,
      {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(metaTagData),
      }
    );

    if (!response.data) {
      throw new Error('Failed to update meta tag');
    }

    return response.data;
  }

  async deleteMetaTag(token: string, id: number): Promise<void> {
    customLog('API Service: deleteMetaTag called with ID:', id);

    await this.request(`/admin/meta/${id}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  // Set logout callback for handling 401 errors
  setLogoutCallback(callback: () => void) {
    this.logoutCallback = callback;
  }
}

export default new SEOService();
